import React from 'react';
import PropTypes from 'prop-types';
import * as moment from 'moment';
import cx from 'classnames';
import translate from '../../../../commonComponents/translate';
import ProfileIcon from '../carousel/icons/ProfileIcon';
import {
  JOURNEY_STEP_STATUS,
  JOURNEY_STEP_TYPE,
  snakeToCamel,
  STEPS_CONFIG,
} from '../../../../services/utilityFunctions';
import JourneyStepProp from '../../propTypes/journeyStepProp';

const journeyStepTitle = (t, step) =>
  step.type.startsWith('profile')
    ? t('journey_stepType_profile')
    : t(`journey_stepType_${step.type}`);

const journeyStepSubtitle = (t, step) => {
  switch (step.type) {
    case JOURNEY_STEP_TYPE.SESSION:
      return step?.session?.name ? step.session.name : t('n/a');
    case JOURNEY_STEP_TYPE.GAME:
      return step.game ? step.game.name : t('n/a');
    case JOURNEY_STEP_TYPE.COURSE:
      return step.course ? step.course.name : t('n/a');
    case JOURNEY_STEP_TYPE.SURVEY:
      return step.survey ? step.survey.title : t('n/a');
    case JOURNEY_STEP_TYPE.TASK:
      return step.title ? step.title : t('n/a');
    default:
      return step.type.startsWith('profile')
        ? t(`profile_tab_${snakeToCamel(step.type.slice(8))}`)
        : t(`journey_stepType_${step.type}`);
  }
};

const journeyStepHasProgress = step => {
  const typesWithoutProgress = [
    JOURNEY_STEP_TYPE.TASK,
    JOURNEY_STEP_TYPE.SURVEY,
    JOURNEY_STEP_TYPE.TROPHY,
    JOURNEY_STEP_TYPE.DOCUMENT,
    JOURNEY_STEP_TYPE.FILL_AND_SIGN,
  ];
  return !typesWithoutProgress.includes(step.type);
};

function JourneyPathItem({ t, step, flipped, onClick }) {
  const isDone = step.status === JOURNEY_STEP_STATUS.DONE;
  const isLocked = step.status === JOURNEY_STEP_STATUS.LOCKED;
  const isWaiting = step.status === JOURNEY_STEP_STATUS.WAITING_APPROVAL;
  const containerClass = cx('journey-path-item', {
    'invert-row': flipped,
    'journey-path-locked': isLocked,
    'journey-path-selectable': !isLocked && !!onClick,
  });
  const numberClass = cx('journey-path-item-number', {
    'journey-path-item-number-right': flipped,
    'step-done': isDone,
    'step-waiting': isWaiting,
  });
  const progressClass = cx('progress-container');
  const finishedClass = cx('date', {
    'text-success': !isLocked && isDone,
    'text-danger': !isLocked && !isDone,
  });

  const progress = step.progress ? Math.round(step.progress) : 0;
  const progressText = `${progress}%`;

  return (
    <div
      className={containerClass}
      onClick={() => {
        if (!isLocked) {
          onClick(step._id);
        }
      }}
    >
      <div className={numberClass}>
        {isDone && <i className="fa fa-check" />}
        {(!isDone || isLocked || isWaiting) && <p>{(step?.pos || 0) + 1}</p>}
      </div>
      <div className="journey-path-item-content">
        <div className="journey-path-item-content-info">
          <div className="journey-path-item-content-info-icon">
            <img
              key={step.type}
              alt={step.type}
              src={STEPS_CONFIG[step.type]?.imageURL}
            />
          </div>
          <div className="journey-path-item-content-info-title">
            <h4>{journeyStepTitle(t, step)}</h4>
            <span style={{ display: 'flex' }}>
              {journeyStepSubtitle(t, step)}
            </span>
          </div>
        </div>
        {journeyStepHasProgress(step) && (
          <div className={progressClass}>
            <span>{progressText}</span>
            <div className="progress">
              <div
                style={{ width: progressText }}
                className={`progress-bar ${progress > 50 ? 'green' : 'orange'}`}
              />
            </div>
            {step.finishedDate && (
              <span className={finishedClass}>
                <img
                  style={{ marginRight: '0.5rem' }}
                  src="/images/journey/calendar.svg"
                  alt="calendar-icon"
                />
                {moment(step.finishedDate).format('DD/MM/YY')}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="journey-path-item-icon">
        <ProfileIcon />
      </div>
    </div>
  );
}

JourneyPathItem.propTypes = {
  t: PropTypes.func.isRequired,
  step: PropTypes.shape(JourneyStepProp.propType).isRequired,
  flipped: PropTypes.bool,
  onClick: PropTypes.func,
};

JourneyPathItem.defaultProps = {
  flipped: false,
  onClick: null,
};

export default translate(JourneyPathItem);
